import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faCalendarAlt,
    faUsers,
    faMoneyBillWave,
    faTrash,
    faSpinner,
    faShieldAlt,
    faSearch,
    faTimes,
    faExternalLinkAlt,
    faChevronDown,
    faChevronUp,
    faSort,
    faSortAmountDown,
    faSortAmountUp,
    faLock,
    faUnlock,
    faClone,
    faCopy,
    faCheck,
    faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../hooks/useAuth';
import { isAdmin } from '../utils/adminConfig';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import { ref, get } from 'firebase/database';
import { database } from '../firebase/firebase';
import { cloneEvent, purgeEvent, isClonedEvent, CloneEventResult } from '../utils/cloneEvent';
import { useFormatCurrency } from '../hooks/useFormatCurrency';

const ITEMS_PER_PAGE = 15;

interface EventSummary {
    id: string;
    name: string;
    createdBy: string;
    createdAt: number;
    participantCount: number;
    expenseCount: number;
    totalAmount: number;
    sponsorAmount: number;
    isClosed: boolean;
    participants: Record<string, string>;
}

type SortField = 'createdAt' | 'name' | 'participantCount' | 'totalAmount';
type SortDir = 'asc' | 'desc';

const EventManagementPage: React.FC = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const { formatCurrency } = useFormatCurrency();
    const [events, setEvents] = useState<EventSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState<SortField>('createdAt');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [eventToDelete, setEventToDelete] = useState<EventSummary | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [cloningEventId, setCloningEventId] = useState<string | null>(null);
    const [cloneResult, setCloneResult] = useState<CloneEventResult | null>(null);
    const [cloneError, setCloneError] = useState<string | null>(null);
    const [copiedCloneId, setCopiedCloneId] = useState(false);

    useEffect(() => {
        if (user && isAdmin(user.email)) {
            loadEvents();
        }
    }, [user]);

    const loadEvents = async () => {
        setLoading(true);
        try {
            // Step 1: Fetch eventList (has parent-level .read: true)
            const eventListRef = ref(database, 'eventList');
            const listSnapshot = await get(eventListRef);

            if (!listSnapshot.exists()) {
                setEvents([]);
                return;
            }

            const eventIds = Object.keys(listSnapshot.val());

            // Step 2: Batch-fetch individual events (each has .read: true)
            const BATCH_SIZE = 10;
            const eventsList: EventSummary[] = [];

            for (let i = 0; i < eventIds.length; i += BATCH_SIZE) {
                const batch = eventIds.slice(i, i + BATCH_SIZE);
                const batchResults = await Promise.all(
                    batch.map(async (id) => {
                        try {
                            // Expenses live in their own top-level node, not under
                            // events/<id> — reading data.expenses always came back empty,
                            // which is why every event showed 0 đ.
                            const [snapshot, expensesSnapshot] = await Promise.all([
                                get(ref(database, `events/${id}`)),
                                get(ref(database, `eventExpenses/${id}`)),
                            ]);
                            if (!snapshot.exists()) return null;

                            const data = snapshot.val();
                            const meta = data.meta || {};
                            const expenses = expensesSnapshot.exists() ? expensesSnapshot.val() : {};
                            const participants = meta.participants || {};

                            let totalAmount = 0;
                            let expenseCount = 0;
                            let sponsorAmount = 0;
                            Object.values(expenses).forEach((expense: any) => {
                                if (expense.type === 'sponsor') {
                                    // Sponsor amounts are stored negative
                                    sponsorAmount += Math.abs(expense.amount || 0);
                                } else {
                                    totalAmount += expense.amount || 0;
                                    expenseCount++;
                                }
                            });

                            return {
                                id,
                                name: meta.name || 'Unnamed Event',
                                createdBy: meta.createdBy || '',
                                createdAt: meta.createdAt || 0,
                                participantCount: Object.keys(participants).length,
                                expenseCount,
                                totalAmount,
                                sponsorAmount,
                                isClosed: listSnapshot.val()[id] === false,
                                participants
                            } as EventSummary;
                        } catch {
                            return null;
                        }
                    })
                );
                eventsList.push(...batchResults.filter((e): e is EventSummary => e !== null));
            }

            setEvents(eventsList);
        } catch (error) {
            console.error('Error loading events:', error);
        } finally {
            setLoading(false);
        }
    };


    const handleDeleteEvent = async () => {
        if (!eventToDelete) return;
        setDeleteLoading(true);
        try {
            // Removing only `events/<id>` used to orphan the expenses, participants
            // and index entries — purgeEvent clears every path the event lives in.
            const failedPaths = await purgeEvent(eventToDelete.id);
            if (failedPaths.length > 0) {
                console.warn('Some paths could not be removed:', failedPaths);
            }
            setEvents(prev => prev.filter(e => e.id !== eventToDelete.id));
            if (cloneResult?.eventId === eventToDelete.id) {
                setCloneResult(null);
            }
            setShowDeleteModal(false);
            setEventToDelete(null);
        } catch (error) {
            console.error('Error deleting event:', error);
            alert(t('admin.events.deleteModal.error'));
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCloneEvent = async (sourceEventId: string) => {
        if (!user) return;

        setCloningEventId(sourceEventId);
        setCloneError(null);
        setCloneResult(null);
        setCopiedCloneId(false);

        try {
            const result = await cloneEvent(sourceEventId, { newOwnerId: user.uid });
            setCloneResult(result);
            await loadEvents();
            setExpandedEvent(result.eventId);
        } catch (error) {
            console.error('Error cloning event:', error);
            setCloneError(error instanceof Error ? error.message : String(error));
        } finally {
            setCloningEventId(null);
        }
    };

    const handleCopyCloneId = async (eventId: string) => {
        try {
            await navigator.clipboard.writeText(eventId);
            setCopiedCloneId(true);
            setTimeout(() => setCopiedCloneId(false), 2000);
        } catch (error) {
            console.error('Clipboard write failed:', error);
        }
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('desc');
        }
    };

    // Filter & sort
    const filteredEvents = useMemo(() => {
        let result = events;

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(e =>
                e.name.toLowerCase().includes(term) ||
                e.id.toLowerCase().includes(term) ||
                Object.values(e.participants).some(name =>
                    name.toLowerCase().includes(term)
                )
            );
        }

        result.sort((a, b) => {
            let cmp = 0;
            switch (sortField) {
                case 'name':
                    cmp = a.name.localeCompare(b.name);
                    break;
                case 'participantCount':
                    cmp = a.participantCount - b.participantCount;
                    break;
                case 'totalAmount':
                    cmp = a.totalAmount - b.totalAmount;
                    break;
                case 'createdAt':
                default:
                    cmp = a.createdAt - b.createdAt;
                    break;
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });

        return result;
    }, [events, searchTerm, sortField, sortDir]);

    // Pagination
    const totalPages = Math.ceil(filteredEvents.length / ITEMS_PER_PAGE);
    const paginatedEvents = filteredEvents.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Stats
    const stats = useMemo(() => ({
        totalEvents: events.length,
        totalParticipants: new Set(events.flatMap(e => Object.keys(e.participants))).size,
        totalAmount: events.reduce((sum, e) => sum + e.totalAmount, 0),
        activeEvents: events.filter(e => !e.isClosed).length,
        closedEvents: events.filter(e => e.isClosed).length,
    }), [events]);

    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleDateString('vi-VN', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    // Auth guards
    if (!user) {
        return (
            <div className="glass-page-bg flex items-center justify-center">
                <div className="glass-card p-8 text-center max-w-md">
                    <FontAwesomeIcon icon={faShieldAlt} className="text-4xl text-yellow-500 mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {t('admin.events.auth.signInRequired')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {t('admin.events.auth.signInMessage')}
                    </p>
                    <Link to="/" className="glass-btn-primary">
                        {t('admin.events.auth.goHome')}
                    </Link>
                </div>
            </div>
        );
    }

    if (!isAdmin(user.email)) {
        return (
            <div className="glass-page-bg flex items-center justify-center">
                <div className="glass-card p-8 text-center max-w-md">
                    <FontAwesomeIcon icon={faShieldAlt} className="text-4xl text-red-500 mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {t('admin.events.auth.accessDenied')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {t('admin.events.auth.adminRequired')}
                    </p>
                    <Link to="/" className="glass-btn-primary">
                        {t('admin.events.auth.goHome')}
                    </Link>
                </div>
            </div>
        );
    }

    if (loading) {
        return <LoadingSpinner />;
    }

    const SortButton = ({ field, label }: { field: SortField; label: string }) => (
        <button
            onClick={() => handleSort(field)}
            className={`flex items-center gap-1 text-xs font-medium transition-colors ${
                sortField === field
                    ? 'text-indigo-600 dark:text-indigo-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
        >
            {label}
            <FontAwesomeIcon
                icon={sortField === field ? (sortDir === 'asc' ? faSortAmountUp : faSortAmountDown) : faSort}
                className="text-[10px]"
            />
        </button>
    );

    return (
        <div className="glass-page-bg">
            <div className="max-w-6xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="glass-card p-4 mb-6">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <Link to="/" className="glass-header-back">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {t('admin.events.header.title')}
                                </h1>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                                        {t('admin.events.header.adminBadge')}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={loadEvents}
                            className="glass-btn-primary !px-3 !py-1.5 text-sm"
                        >
                            {t('admin.events.header.refresh')}
                        </button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                    <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalEvents}</div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.events.stats.totalEvents')}</p>
                    </div>
                    <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.activeEvents}</div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.events.stats.active')}</p>
                    </div>
                    <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-gray-500 dark:text-gray-400">{stats.closedEvents}</div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.events.stats.closed')}</p>
                    </div>
                    <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.totalParticipants}</div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.events.stats.totalUsers')}</p>
                    </div>
                    <div className="glass-card p-3 text-center col-span-2 sm:col-span-1">
                        <div className="text-lg font-bold text-purple-600 dark:text-purple-400 truncate">{formatCurrency(stats.totalAmount)}</div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.events.stats.totalSpent')}</p>
                    </div>
                </div>

                {/* Search & Sort */}
                <div className="glass-card p-4 mb-4">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                placeholder={t('admin.events.filters.searchPlaceholder')}
                                className="w-full pl-9 pr-8 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            <SortButton field="createdAt" label={t('admin.events.sort.date')} />
                            <SortButton field="name" label={t('admin.events.sort.name')} />
                            <SortButton field="participantCount" label={t('admin.events.sort.participants')} />
                            <SortButton field="totalAmount" label={t('admin.events.sort.amount')} />
                        </div>
                    </div>
                </div>

                {/* Clone result */}
                {cloneResult && (
                    <div className="glass-card p-4 mb-4 border-l-4 border-emerald-500 animate-slide-down">
                        <div className="flex items-start gap-3">
                            <FontAwesomeIcon icon={faClone} className="text-emerald-500 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm text-gray-900 dark:text-white">
                                    {t('admin.events.clone.success')}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 truncate">
                                    {cloneResult.name}
                                </div>
                                <div className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-1 break-all">
                                    {cloneResult.eventId}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    {t('admin.events.clone.summary', {
                                        participants: cloneResult.participantCount,
                                        expenses: cloneResult.expenseCount
                                    })}
                                    {cloneResult.ownerSeatTaken && ` ${t('admin.events.clone.ownerSeat')}`}
                                </div>
                                {cloneResult.warnings.map((warning, i) => (
                                    <div key={i} className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 flex items-start gap-1.5">
                                        <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 flex-shrink-0" />
                                        <span>{warning}</span>
                                    </div>
                                ))}
                                <div className="flex flex-wrap items-center gap-2 mt-3">
                                    <Link
                                        to={`/event/${cloneResult.eventId}`}
                                        className="glass-btn-primary !px-3 !py-1.5 text-xs flex items-center gap-1.5"
                                    >
                                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                                        {t('admin.events.clone.openClone')}
                                    </Link>
                                    <button
                                        onClick={() => handleCopyCloneId(cloneResult.eventId)}
                                        className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1.5"
                                    >
                                        <FontAwesomeIcon icon={copiedCloneId ? faCheck : faCopy} />
                                        {copiedCloneId ? t('admin.events.clone.copied') : t('admin.events.clone.copyId')}
                                    </button>
                                    <button
                                        onClick={() => setCloneResult(null)}
                                        className="px-3 py-1.5 text-xs rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        {t('admin.events.clone.dismiss')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {cloneError && (
                    <div className="glass-card p-4 mb-4 border-l-4 border-red-500">
                        <div className="flex items-start gap-3">
                            <FontAwesomeIcon icon={faTriangleExclamation} className="text-red-500 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm text-gray-900 dark:text-white">
                                    {t('admin.events.clone.error')}
                                </div>
                                <div className="text-xs font-mono text-red-600 dark:text-red-400 mt-1 break-all">
                                    {cloneError}
                                </div>
                            </div>
                            <button
                                onClick={() => setCloneError(null)}
                                className="text-xs text-gray-500 dark:text-gray-400 hover:underline flex-shrink-0"
                            >
                                {t('admin.events.clone.dismiss')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Events List */}
                <div className="space-y-2">
                    {paginatedEvents.length === 0 ? (
                        <div className="glass-card p-8 text-center">
                            <FontAwesomeIcon icon={faCalendarAlt} className="text-3xl text-gray-400 mb-3" />
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                                {t('admin.events.emptyState.title')}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {searchTerm
                                    ? t('admin.events.emptyState.noMatches')
                                    : t('admin.events.emptyState.noEvents')}
                            </p>
                        </div>
                    ) : (
                        paginatedEvents.map(event => (
                            <div key={event.id} className="glass-card overflow-hidden">
                                <div
                                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                                    onClick={() => setExpandedEvent(prev => prev === event.id ? null : event.id)}
                                >
                                    {/* Status icon */}
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                        event.isClosed
                                            ? 'bg-gray-100 dark:bg-gray-700'
                                            : 'bg-green-100 dark:bg-green-900/30'
                                    }`}>
                                        <FontAwesomeIcon
                                            icon={event.isClosed ? faLock : faUnlock}
                                            className={`text-xs ${event.isClosed ? 'text-gray-500' : 'text-green-600 dark:text-green-400'}`}
                                        />
                                    </div>

                                    {/* Event info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm text-gray-900 dark:text-white truncate flex items-center gap-2">
                                            {isClonedEvent(event.id) && (
                                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 flex-shrink-0">
                                                    {t('admin.events.clone.badge')}
                                                </span>
                                            )}
                                            <span className="truncate">{event.name}</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            <span className="flex items-center gap-1">
                                                <FontAwesomeIcon icon={faUsers} className="text-[10px]" />
                                                {event.participantCount}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <FontAwesomeIcon icon={faMoneyBillWave} className="text-[10px]" />
                                                {event.expenseCount} {t('admin.events.table.expenses')}
                                            </span>
                                            <span className="hidden sm:inline">
                                                {formatDate(event.createdAt)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Amount */}
                                    <div className="flex-shrink-0 text-right">
                                        <div className="text-sm font-bold text-purple-600 dark:text-purple-400">
                                            {formatCurrency(event.totalAmount)}
                                        </div>
                                        {event.sponsorAmount > 0 && (
                                            <div className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                                                −{formatCurrency(event.sponsorAmount)}
                                            </div>
                                        )}
                                    </div>

                                    {/* Chevron */}
                                    <FontAwesomeIcon
                                        icon={expandedEvent === event.id ? faChevronUp : faChevronDown}
                                        className="text-xs text-gray-400 flex-shrink-0"
                                    />
                                </div>

                                {/* Expanded details */}
                                {expandedEvent === event.id && (
                                    <div className="px-4 pb-3 pt-1 border-t border-gray-200 dark:border-gray-700 animate-slide-down">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                            <div className="glass-sub-panel rounded-lg p-3">
                                                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                                    {t('admin.events.detail.eventId')}
                                                </div>
                                                <div className="text-xs font-mono text-gray-900 dark:text-white break-all">
                                                    {event.id}
                                                </div>
                                            </div>
                                            <div className="glass-sub-panel rounded-lg p-3">
                                                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                                    {t('admin.events.detail.createdAt')}
                                                </div>
                                                <div className="text-xs text-gray-900 dark:text-white">
                                                    {formatDate(event.createdAt)}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Participants */}
                                        <div className="glass-sub-panel rounded-lg p-3 mb-3">
                                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                                {t('admin.events.detail.participants')} ({event.participantCount})
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {Object.values(event.participants).map((name, i) => (
                                                    <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                                                        {name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-2">
                                            <Link
                                                to={`/event/${event.id}`}
                                                className="glass-btn-primary !px-3 !py-1.5 text-xs flex items-center gap-1.5"
                                            >
                                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                                                {t('admin.events.actions.viewEvent')}
                                            </Link>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEventToDelete(event);
                                                    setShowDeleteModal(true);
                                                }}
                                                className="px-3 py-1.5 text-xs rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors flex items-center gap-1.5"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                                {t('admin.events.actions.delete')}
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCloneEvent(event.id);
                                                }}
                                                disabled={cloningEventId !== null}
                                                title={t('admin.events.clone.hint')}
                                                className="px-3 py-1.5 text-xs rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 disabled:opacity-40 transition-colors flex items-center gap-1.5"
                                            >
                                                <FontAwesomeIcon
                                                    icon={cloningEventId === event.id ? faSpinner : faClone}
                                                    className={cloningEventId === event.id ? 'animate-spin' : ''}
                                                />
                                                {cloningEventId === event.id
                                                    ? t('admin.events.actions.cloning')
                                                    : t('admin.events.actions.clone')}
                                            </button>
                                        </div>
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
                                            {t('admin.events.clone.hint')}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="glass-card p-3 mt-4 flex items-center justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {t('admin.events.pagination.showing', {
                                start: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                                end: Math.min(currentPage * ITEMS_PER_PAGE, filteredEvents.length),
                                total: filteredEvents.length
                            })}
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="px-2.5 py-1 rounded-lg text-xs font-medium disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                            >
                                ←
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                .map((page, i, arr) => (
                                    <React.Fragment key={page}>
                                        {i > 0 && arr[i - 1] !== page - 1 && (
                                            <span className="text-xs text-gray-400">…</span>
                                        )}
                                        <button
                                            onClick={() => setCurrentPage(page)}
                                            className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                                                currentPage === page
                                                    ? 'bg-indigo-500 text-white'
                                                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    </React.Fragment>
                                ))}
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="px-2.5 py-1 rounded-lg text-xs font-medium disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                            >
                                →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && eventToDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="glass-card p-6 max-w-md w-full animate-fade-in">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                            {t('admin.events.deleteModal.title')}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                            {t('admin.events.deleteModal.confirmation')}
                        </p>
                        <div className="glass-sub-panel rounded-lg p-3 mb-4">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">{eventToDelete.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                ID: {eventToDelete.id}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {eventToDelete.participantCount} {t('admin.events.detail.participants').toLowerCase()} · {formatCurrency(eventToDelete.totalAmount)}
                            </p>
                        </div>
                        <div className="flex items-center gap-3 justify-end">
                            <button
                                onClick={() => { setShowDeleteModal(false); setEventToDelete(null); }}
                                className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                {t('admin.events.deleteModal.cancel')}
                            </button>
                            <button
                                onClick={handleDeleteEvent}
                                disabled={deleteLoading}
                                className="px-4 py-2 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {deleteLoading && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                                {t('admin.events.deleteModal.delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventManagementPage;
